import React, { useState, useEffect } from 'react';
import { Box, Switch, TextField, Modal, Typography, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useToast } from 'use-toast-mui';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonSelect from '../../../../form-components/CommonSelect';

import {
  AutoSettingContainer,
  ModalHeader,
  ModalHeaderTitle,
  AutoSettingContent,
  ModalFooter,
} from './GroupLessonTemplateStyled';

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i.toString().padStart(2, '0'),
  label: i.toString().padStart(2, '0'),
}));

// 폐강 시점용: 1~23시간
const CLOSE_HOUR_OPTIONS = Array.from({ length: 23 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}`,
}));

const MINUTE_OPTIONS = ['00', '10', '20', '30', '40', '50'].map(v => ({
  value: v,
  label: v,
}));

const tealToggleSx = {
  width: 36, height: 20, padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '2px',
    '&.Mui-checked': {
      transform: 'translateX(16px)', color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: '#4AB3BC', opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': { width: 16, height: 16, boxShadow: 'none' },
  '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#ccc', opacity: 1 },
};

const compactInput = {
  '& .MuiOutlinedInput-root': { height: '32px', fontSize: '12px' },
};

const compactSelect = {
  '& .MuiOutlinedInput-root': { height: '32px', fontSize: '12px' },
  '& .MuiSelect-select': { py: '4px !important', fontSize: '12px' },
};

const errorBorder = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d32f2f !important', borderWidth: '2px !important' },
};

const SectionHeader = ({ title, checked, onChange }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    pb: '10px', mb: '14px', borderBottom: '1px solid #eee',
  }}>
    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#333' }}>{title}</Typography>
    <Switch checked={checked} onChange={onChange} sx={tealToggleSx} />
  </Box>
);

const FieldLabel = ({ children, required }) => (
  <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#888', mb: '6px' }}>
    {children}{required && <span style={{ color: '#ef5350', marginLeft: '2px' }}>*</span>}
  </Typography>
);

const UnitText = ({ children }) => (
  <Typography sx={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>{children}</Typography>
);

const SELECT_WIDTH = 64;

// 자동공개 방식. FIXED = 지정 일시에 전체 공개(openScheduledDay 에 YYYY-MM-DD), BEFORE_DAYS = 수업 N일 전.
export const OPEN_SCHEDULE_TYPE = { FIXED: 'FIXED', BEFORE_DAYS: 'BEFORE_DAYS' };

export const OPEN_SCHEDULE_TYPE_OPTIONS = [
  { value: OPEN_SCHEDULE_TYPE.BEFORE_DAYS, label: '수업별 N일 전 공개' },
  { value: OPEN_SCHEDULE_TYPE.FIXED, label: '지정 일시 전체 공개' },
];

/** 저장된 수업 값 → 모달 설정값. 기본은 기존 방식인 수업별 N일 전이다. */
export const toOpenScheduleSettings = lesson => {
  if (lesson?.openScheduledYn !== 'Y') {
    return { openScheduleType: OPEN_SCHEDULE_TYPE.BEFORE_DAYS, openDate: '', openBeforeDays: '' };
  }
  const isFixed = lesson?.openScheduleType === OPEN_SCHEDULE_TYPE.FIXED;
  return {
    openScheduleType: isFixed ? OPEN_SCHEDULE_TYPE.FIXED : OPEN_SCHEDULE_TYPE.BEFORE_DAYS,
    openDate: isFixed ? lesson?.openScheduledDay || '' : '',
    openBeforeDays: !isFixed && lesson?.openScheduledDay != null ? String(lesson.openScheduledDay) : '',
  };
};

/** 모달 설정값 → API 요청 필드 */
export const toOpenSchedulePayload = settings => {
  if (settings.openScheduledYn !== 'Y') {
    return { openScheduleType: null, openScheduledDay: null, openScheduledTime: null };
  }
  const isFixed = settings.openScheduleType === OPEN_SCHEDULE_TYPE.FIXED;
  return {
    openScheduleType: settings.openScheduleType,
    openScheduledDay: isFixed ? settings.openDate || null : settings.openBeforeDays || null,
    openScheduledTime: settings.openHour ? `${settings.openHour}:${settings.openMinute || '00'}` : null,
  };
};

/** 확인 화면 표기 */
export const formatOpenSchedule = settings => {
  const time = `${settings.openHour || '00'}:${settings.openMinute || '00'}`;
  return settings.openScheduleType === OPEN_SCHEDULE_TYPE.FIXED
    ? `${settings.openDate || '-'} ${time} 전체 공개`
    : `수업 ${settings.openBeforeDays || '-'}일 전 ${time}`;
};

const AutoPublishCloseModal = ({ open, onClose, settings, onSave, maxDate }) => {
  const toast = useToast();

  const [openScheduledYn, setOpenScheduledYn] = useState('N');
  const [openScheduleType, setOpenScheduleType] = useState(OPEN_SCHEDULE_TYPE.BEFORE_DAYS);
  const [openDate, setOpenDate] = useState('');
  const [openBeforeDays, setOpenBeforeDays] = useState('');
  const [openHour, setOpenHour] = useState('');
  const [openMinute, setOpenMinute] = useState('');

  const [closeScheduledYn, setCloseScheduledYn] = useState('N');
  const [closeHour, setCloseHour] = useState('');
  const [closeMinCount, setCloseMinCount] = useState('');

  const [errorField, setErrorField] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open && settings) {
      setOpenScheduledYn(settings.openScheduledYn || 'N');
      setOpenScheduleType(settings.openScheduleType || OPEN_SCHEDULE_TYPE.BEFORE_DAYS);
      setOpenDate(settings.openDate || '');
      setOpenBeforeDays(settings.openBeforeDays || '');
      setOpenHour(settings.openHour || '');
      setOpenMinute(settings.openMinute || '');
      setCloseScheduledYn(settings.closeScheduledYn || 'N');
      setCloseHour(settings.closeHour || '');
      setCloseMinCount(settings.closeMinCount || '');
      setErrorField(null);
    }
  }, [open, settings]);

  const handleSave = () => {
    if (openScheduledYn === 'Y') {
      const isFixed = openScheduleType === OPEN_SCHEDULE_TYPE.FIXED;
      if (isFixed && !openDate) { setErrorField('openDate'); toast.error('공개 날짜를 선택해주세요'); return; }
      if (!isFixed && openBeforeDays === '') { setErrorField('openBeforeDays'); toast.error('며칠 전에 공개할지 입력해주세요'); return; }
      if (!openHour) { setErrorField('openHour'); toast.error('공개 시간을 선택해주세요'); return; }
      if (!openMinute) { setErrorField('openMinute'); toast.error('공개 분을 선택해주세요'); return; }
    }
    if (closeScheduledYn === 'Y') {
      if (!closeHour) { setErrorField('closeHour'); toast.error('폐강 시간을 선택해주세요'); return; }
      if (!closeMinCount) { setErrorField('closeMinCount'); toast.error('폐강 최소 인원을 입력해주세요'); return; }
    }

    onSave({
      openScheduledYn,
      openScheduleType,
      openDate: openScheduledYn === 'Y' && openScheduleType === OPEN_SCHEDULE_TYPE.FIXED ? openDate : '',
      openBeforeDays: openScheduledYn === 'Y' && openScheduleType === OPEN_SCHEDULE_TYPE.BEFORE_DAYS ? openBeforeDays : '',
      openHour: openScheduledYn === 'Y' ? openHour : '',
      openMinute: openScheduledYn === 'Y' ? openMinute : '',
      closeScheduledYn,
      closeHour: closeScheduledYn === 'Y' ? closeHour : '',
      closeMinCount: closeScheduledYn === 'Y' ? closeMinCount : '',
    });
    onClose();
  };

  return (
    <>
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <AutoSettingContainer>
        <ModalHeader>
          <ModalHeaderTitle>자동 공개 · 폐강 설정</ModalHeaderTitle>
          <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </ModalHeader>

        <AutoSettingContent>
          {/* 수업 공개 설정 */}
          <Box sx={{ mb: '24px' }}>
            <SectionHeader
              title="수업 자동공개 설정"
              checked={openScheduledYn === 'Y'}
              onChange={e => {
                if (e.target.checked) {
                  setConfirmOpen(true);
                } else {
                  setOpenScheduledYn('N');
                  setOpenDate('');
                  setOpenBeforeDays('');
                  setOpenHour('');
                  setOpenMinute('');
                }
              }}
            />

              <Box sx={{ mb: '14px', opacity: openScheduledYn === 'Y' ? 1 : 0.4, pointerEvents: openScheduledYn === 'Y' ? 'auto' : 'none' }}>
                <FieldLabel required>공개 방식</FieldLabel>
                <Box sx={{ display: 'flex', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                  {OPEN_SCHEDULE_TYPE_OPTIONS.map(({ value, label }) => (
                    <Box
                      key={value}
                      onClick={() => { setOpenScheduleType(value); setErrorField(null); }}
                      sx={{
                        flex: 1, height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '12px', cursor: 'pointer',
                        ...(openScheduleType === value
                          ? { backgroundColor: '#4AB3BC', color: '#fff', fontWeight: 600 }
                          : { backgroundColor: '#fff', color: '#666' }),
                      }}
                    >
                      {label}
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: '11px', color: '#888', mt: '6px' }}>
                  {openScheduleType === OPEN_SCHEDULE_TYPE.FIXED
                    ? '등록하는 수업 전체가 이 날짜·시각에 한꺼번에 공개됩니다.'
                    : '수업마다 수업일 기준 N일 전 이 시각에 공개됩니다.'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: '14px', alignItems: 'flex-end', opacity: openScheduledYn === 'Y' ? 1 : 0.4, pointerEvents: openScheduledYn === 'Y' ? 'auto' : 'none' }}>
                {openScheduleType === OPEN_SCHEDULE_TYPE.BEFORE_DAYS ? (
                <Box sx={{ flex: 1 }}>
                  <FieldLabel required>공개 시점</FieldLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <UnitText>수업</UnitText>
                    <TextField
                      size="small"
                      value={openBeforeDays}
                      onChange={e => { setOpenBeforeDays(e.target.value.replace(/[^0-9]/g, '')); if (errorField === 'openBeforeDays') setErrorField(null); }}
                      placeholder="0"
                      sx={{ width: '52px', ...compactInput, ...(errorField === 'openBeforeDays' ? errorBorder : {}) }}
                    />
                    <UnitText>일 전</UnitText>
                  </Box>
                </Box>
                ) : (
                <Box sx={{ flex: 1 }}>
                  <FieldLabel required>공개 날짜</FieldLabel>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
                    <DatePicker
                      minDate={new Date()}
                      maxDate={maxDate ? new Date(maxDate) : undefined}
                      value={openDate ? new Date(openDate) : null}
                      onChange={date => {
                        if (date) {
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, '0');
                          const d = String(date.getDate()).padStart(2, '0');
                          setOpenDate(`${y}-${m}-${d}`);
                        } else {
                          setOpenDate('');
                        }
                        if (errorField === 'openDate') setErrorField(null);
                      }}
                      inputFormat="yyyy-MM-dd"
                      renderInput={params => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          placeholder="날짜 선택"
                          sx={{
                            ...compactInput,
                            ...(errorField === 'openDate' ? errorBorder : {}),
                            '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '32px', fontSize: '12px' },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Box>
                )}
                <Box>
                  <FieldLabel required>공개 시간</FieldLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Box sx={{ ...compactSelect, ...(errorField === 'openHour' ? errorBorder : {}) }}>
                      <CommonSelect
                        value={openHour}
                        onChange={e => { setOpenHour(e.target.value); if (errorField === 'openHour') setErrorField(null); }}
                        options={HOUR_OPTIONS}
                        emptyLabel="시"
                        width={SELECT_WIDTH}
                      />
                    </Box>
                    <UnitText>:</UnitText>
                    <Box sx={{ ...compactSelect, ...(errorField === 'openMinute' ? errorBorder : {}) }}>
                      <CommonSelect
                        value={openMinute}
                        onChange={e => { setOpenMinute(e.target.value); if (errorField === 'openMinute') setErrorField(null); }}
                        options={MINUTE_OPTIONS}
                        emptyLabel="분"
                        width={SELECT_WIDTH}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
          </Box>

          {/* 수업 폐강 설정 */}
          <Box>
            <SectionHeader
              title="수업 자동폐강 설정"
              checked={closeScheduledYn === 'Y'}
              onChange={e => {
                setCloseScheduledYn(e.target.checked ? 'Y' : 'N');
                if (!e.target.checked) {
                  setCloseHour('');
                  setCloseMinCount('');
                }
              }}
            />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', opacity: closeScheduledYn === 'Y' ? 1 : 0.4, pointerEvents: closeScheduledYn === 'Y' ? 'auto' : 'none' }}>
                <Box>
                  <FieldLabel required>폐강 시점</FieldLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <UnitText>수업 시작</UnitText>
                    <Box sx={{ ...compactSelect, ...(errorField === 'closeHour' ? errorBorder : {}) }}>
                      <CommonSelect
                        value={closeHour}
                        onChange={e => { setCloseHour(e.target.value); if (errorField === 'closeHour') setErrorField(null); }}
                        options={CLOSE_HOUR_OPTIONS}
                        emptyLabel="시간"
                        width={SELECT_WIDTH}
                      />
                    </Box>
                    <UnitText>시간 전 판단</UnitText>
                  </Box>
                </Box>
                <Box>
                  <FieldLabel required>폐강 조건</FieldLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <UnitText>예약 인원이</UnitText>
                    <TextField
                      size="small"
                      value={closeMinCount}
                      onChange={e => { setCloseMinCount(e.target.value.replace(/[^0-9]/g, '')); if (errorField === 'closeMinCount') setErrorField(null); }}
                      placeholder="0"
                      sx={{ width: '52px', ...compactInput, ...(errorField === 'closeMinCount' ? errorBorder : {}) }}
                    />
                    <UnitText>명 이하이면 폐강</UnitText>
                  </Box>
                </Box>
              </Box>
          </Box>
        </AutoSettingContent>

        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleSave}>
            설정 완료
          </CommonModalButton>
        </ModalFooter>
      </AutoSettingContainer>
    </Modal>

    {/* 자동공개 확인 다이얼로그 */}
    <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>수업 자동공개 설정</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: '14px', color: '#333', mb: '8px' }}>
          자동공개를 설정하시겠습니까?
        </Typography>
        <Typography sx={{ fontSize: '13px', color: '#888' }}>
          설정 시 모든 수업이 <b style={{ color: '#D32F2F' }}>비공개</b>로 생성되며, 지정한 공개 방식에 따라 자동으로 공개됩니다.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: '12px 24px' }}>
        <CommonModalButton variant="cancel" onClick={() => setConfirmOpen(false)}>취소</CommonModalButton>
        <CommonModalButton variant="submit" onClick={() => { setOpenScheduledYn('Y'); setConfirmOpen(false); }}>설정하기</CommonModalButton>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AutoPublishCloseModal;
